/**
 * Sun position — a tiny, dependency-free solar calculator so the chart can pick
 * its palette from where the sun actually is, rather than the clock alone.
 *
 * We compute the sun's altitude (its angle above the horizon) for a given
 * position and instant, then classify it. Working from the live altitude rather
 * than pre-computing sunrise/sunset times is the robust way to do it: it falls
 * out correct at every latitude, including the high-latitude cases where the sun
 * never rises or never sets, without any special-casing.
 *
 * The thresholds follow the usual conventions:
 *   - the geometric horizon sits at about -0.833 deg once you allow for the
 *     sun's own radius and atmospheric refraction, so sunrise/sunset is there,
 *     not at a flat 0 deg;
 *   - civil twilight runs from that horizon down to -6 deg — the band where it's
 *     no longer full daylight but you can still see, which is exactly our dusk.
 *
 * Formulae are the standard low-precision NOAA solar equations (good to well
 * under a minute for our purposes). Angles are in degrees unless noted.
 */

/** Sun sits on the visible horizon at ~-0.833 deg (radius + refraction). */
export const SUNRISE_ALT_DEG = -0.833;
/** Civil twilight reaches down to -6 deg; below that we call it night. */
export const CIVIL_TWILIGHT_ALT_DEG = -6;

const DEG = Math.PI / 180;
const norm360 = (d: number): number => ((d % 360) + 360) % 360;

/**
 * The sun's altitude above the horizon, in degrees, at a position and time.
 * Positive is up. Latitude/longitude in degrees, longitude east-positive.
 */
export function sunAltitudeDeg(latDeg: number, lonDeg: number, date: Date = new Date()): number {
  // Days (with fraction) since the J2000.0 epoch, 2000-01-01 12:00 UT.
  const jd = date.getTime() / 86400000 + 2440587.5;
  const n = jd - 2451545.0;

  // Sun's ecliptic position.
  const g = norm360(357.529 + 0.98560028 * n) * DEG; // mean anomaly
  const q = norm360(280.459 + 0.98564736 * n); // mean longitude
  const L = (q + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * DEG; // ecliptic longitude
  const e = (23.439 - 0.00000036 * n) * DEG; // obliquity of the ecliptic

  const sinDec = Math.sin(e) * Math.sin(L);
  const dec = Math.asin(sinDec); // declination, radians
  const ra = Math.atan2(Math.cos(e) * Math.sin(L), Math.cos(L)); // right ascension, radians

  // Greenwich mean sidereal time, then the local hour angle of the sun.
  const gmst = norm360(280.46061837 + 360.98564736629 * n); // degrees
  const raDeg = norm360((ra / DEG));
  const ha = norm360(gmst + lonDeg - raDeg) * DEG; // hour angle, radians

  const lat = latDeg * DEG;
  const sinAlt = Math.sin(lat) * Math.sin(dec) + Math.cos(lat) * Math.cos(dec) * Math.cos(ha);
  return Math.asin(Math.max(-1, Math.min(1, sinAlt))) / DEG;
}

export type SunPalette = 'day' | 'dusk' | 'night';

/**
 * Which palette the sun says we should be on right now:
 *   - day:   sun on or above the sunrise/sunset horizon;
 *   - dusk:  sun in the civil-twilight band either side of that horizon;
 *   - night: sun below civil twilight.
 */
export function paletteForSun(latDeg: number, lonDeg: number, date: Date = new Date()): SunPalette {
  const alt = sunAltitudeDeg(latDeg, lonDeg, date);
  if (alt >= SUNRISE_ALT_DEG) return 'day';
  if (alt >= CIVIL_TWILIGHT_ALT_DEG) return 'dusk';
  return 'night';
}
